import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Expand,
  MapPin,
  Ruler,
  Sparkles,
  User,
  X
} from 'lucide-react';
import { getProjectBySlug, getProjects } from '../utils/cms';
import { FadeIn } from '../components/ScrollReveal';
import { trackPageView, trackProjectView } from '../utils/analytics';
import Seo from '../components/Seo';

const categoryLabels = {
  bathroom: 'Bad & Wellness',
  kitchen: 'Küche',
  floor: 'Boden',
  outdoor: 'Außenbereich',
  special: 'Besonderes Projekt'
};

const ProjectDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const projectData = getProjectBySlug(slug);

    if (!projectData) {
      navigate('/');
      return;
    }

    setProject(projectData);
    setLightboxIndex(null);
    trackProjectView(projectData.title, projectData.category);
    trackPageView(`/projekte/${slug}`, `Projekt: ${projectData.title}`);
  }, [slug, navigate]);

  const images = useMemo(() => {
    if (!project) return [];
    return [...new Set([project.image, ...(project.gallery || [])].filter(Boolean))];
  }, [project]);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxIndex(null);
      if (event.key === 'ArrowRight') {
        setLightboxIndex((index) => (index + 1) % images.length);
      }
      if (event.key === 'ArrowLeft') {
        setLightboxIndex((index) => (index - 1 + images.length) % images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxIndex, images.length]);

  if (!project) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-stone-50">
        <p className="text-sm font-medium uppercase tracking-[0.2em] text-stone-500">
          Projekt wird geladen …
        </p>
      </div>
    );
  }

  const allProjects = getProjects();
  const currentIndex = allProjects.findIndex((item) => item.slug === slug);
  const previousProject = currentIndex > 0 ? allProjects[currentIndex - 1] : null;
  const nextProject = currentIndex >= 0 && currentIndex < allProjects.length - 1
    ? allProjects[currentIndex + 1]
    : null;

  const projectFacts = [
    project.client && { icon: User, label: 'Auftrag', value: project.client },
    project.location && { icon: MapPin, label: 'Ort', value: project.location },
    project.duration && { icon: Clock, label: 'Dauer', value: project.duration },
    project.area_size && { icon: Ruler, label: 'Fläche', value: project.area_size }
  ].filter(Boolean);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long'
  });

  const openLightbox = (index) => setLightboxIndex(index);
  const moveLightbox = (direction) => {
    setLightboxIndex((index) => (
      direction === 'next'
        ? (index + 1) % images.length
        : (index - 1 + images.length) % images.length
    ));
  };

  const thumbnailImages = images.slice(1, 7);
  const hiddenImageCount = Math.max(images.length - 7, 0);
  const projectUrl = `https://fliesen-jarek.de/projekte/${project.slug}`;
  const projectSeoNames = {
    '05-modernes-badezimmer-dusche': 'Badezimmer mit bodengleicher Dusche',
    '06-modernes-badezimmer-waben': 'Badezimmer mit Wabenfliesen'
  };
  const projectSeoName = projectSeoNames[project.slug] || project.title;
  const projectSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CreativeWork',
        '@id': `${projectUrl}/#project`,
        'name': project.title,
        'headline': project.title,
        'description': project.description,
        'url': projectUrl,
        'image': images.map((image) => `https://fliesen-jarek.de${encodeURI(image)}`),
        'dateCreated': project.date,
        'inLanguage': 'de-DE',
        'locationCreated': project.location ? { '@type': 'Place', 'name': project.location } : undefined,
        'creator': { '@id': 'https://fliesen-jarek.de/#business' },
        'about': ['Fliesenverlegung', 'Badsanierung', ...(project.materials || [])]
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${projectUrl}/#breadcrumb`,
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Startseite', 'item': 'https://fliesen-jarek.de/' },
          { '@type': 'ListItem', 'position': 2, 'name': 'Projekte', 'item': 'https://fliesen-jarek.de/#gallery' },
          { '@type': 'ListItem', 'position': 3, 'name': project.title, 'item': projectUrl }
        ]
      }
    ]
  };

  return (
    <>
      <Seo
        title={`${projectSeoName} in ${project.location || 'Cuxhaven'}`}
        description={`${project.description}. Referenzprojekt von Fliesen Jarek – Fliesenleger für Cuxhaven und Umgebung.`}
        image={images[0]}
        imageAlt={`${project.title} – ausgeführt von Fliesen Jarek`}
        type="article"
        schema={projectSchema}
      />

      <article className="min-h-screen overflow-hidden bg-[#f7f5f1]">
        <header className="relative bg-stone-950 text-white">
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute -right-32 -top-40 h-96 w-96 rounded-full bg-primary-600/20 blur-3xl" />
            <div className="absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-amber-100/10 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-8 sm:px-6 sm:pb-24 sm:pt-10 lg:px-8 lg:pb-28">
            <Link
              to="/#gallery"
              className="group inline-flex items-center gap-2 text-sm font-medium text-stone-300 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              Alle Projekte
            </Link>

            <div className="mt-12 grid items-end gap-10 lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-16">
              <div>
                <div className="mb-5 flex flex-wrap items-center gap-3">
                  <span className="rounded-full border border-primary-400/40 bg-primary-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">
                    {categoryLabels[project.category] || 'Referenzprojekt'}
                  </span>
                  {project.date && (
                    <span className="inline-flex items-center gap-2 text-sm text-stone-400">
                      <Calendar className="h-4 w-4" />
                      {formatDate(project.date)}
                    </span>
                  )}
                </div>

                <h1 className="max-w-4xl font-display text-4xl font-semibold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-7xl">
                  {project.title}
                </h1>
                <p className="mt-6 max-w-3xl text-lg leading-relaxed text-stone-300 sm:text-xl">
                  {project.description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10">
                {projectFacts.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="bg-stone-900/80 p-4 backdrop-blur-sm sm:p-5">
                    <Icon className="mb-3 h-5 w-5 text-primary-300" />
                    <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-stone-500">
                      {label}
                    </p>
                    <p className="mt-1 text-sm font-medium leading-snug text-white sm:text-base">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </header>

        <section className="relative z-10 -mt-10 pb-16 sm:-mt-12 lg:pb-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="group relative block aspect-[4/3] w-full overflow-hidden rounded-2xl bg-stone-200 text-left shadow-2xl shadow-stone-950/15 sm:aspect-[16/10] lg:aspect-[16/8] lg:rounded-3xl"
              aria-label="Hauptbild groß anzeigen"
            >
              <img
                src={images[0]}
                alt={project.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.025]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 flex w-full items-end justify-between gap-4 p-5 sm:p-8">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">Ausgeführtes Projekt</p>
                  <p className="mt-1 text-base font-medium text-white sm:text-lg">{project.location || 'Region Cuxhaven'}</p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-stone-900 shadow-lg transition-transform group-hover:scale-105">
                  <Expand className="h-4 w-4" />
                  <span className="hidden sm:inline">Galerie öffnen</span>
                  <span>{images.length}</span>
                </span>
              </div>
            </button>

            {thumbnailImages.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:mt-5 sm:grid-cols-3 sm:gap-5">
                {thumbnailImages.map((image, index) => {
                  const isLastVisible = index === thumbnailImages.length - 1;
                  return (
                    <button
                      key={image}
                      type="button"
                      onClick={() => openLightbox(index + 1)}
                      className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-stone-200 sm:rounded-2xl"
                      aria-label={`Bild ${index + 2} groß anzeigen`}
                    >
                      <img
                        src={image}
                        alt={`${project.title} – Ansicht ${index + 2}`}
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105 group-hover:brightness-90"
                      />
                      <span className="absolute right-3 top-3 rounded-full bg-stone-950/65 p-2 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                        <Expand className="h-4 w-4" />
                      </span>
                      {isLastVisible && hiddenImageCount > 0 && (
                        <span className="absolute inset-0 flex items-center justify-center bg-stone-950/65 text-xl font-semibold text-white backdrop-blur-[1px]">
                          +{hiddenImageCount} weitere
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {(project.before_image || project.after_image) && (
          <section className="pb-16 lg:pb-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mb-8">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-700">Transformation</p>
                <h2 className="mt-2 font-display text-3xl font-semibold text-stone-900 sm:text-4xl">Vorher & nachher</h2>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                {project.before_image && (
                  <figure className="overflow-hidden rounded-2xl bg-white shadow-sm">
                    <img src={project.before_image} alt={`${project.title} vor der Arbeit`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                    <figcaption className="px-5 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-stone-500">Vorher</figcaption>
                  </figure>
                )}
                {project.after_image && (
                  <figure className="overflow-hidden rounded-2xl bg-white shadow-sm">
                    <img src={project.after_image} alt={`${project.title} nach der Arbeit`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                    <figcaption className="px-5 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-primary-700">Nachher</figcaption>
                  </figure>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="bg-white py-16 sm:py-20 lg:py-28">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_23rem] lg:gap-20 lg:px-8">
            <div className="min-w-0">
              <FadeIn>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-700">Projektgeschichte</p>
                {project.body ? (
                  <div
                    className="project-story mt-5"
                    dangerouslySetInnerHTML={{ __html: project.body }}
                  />
                ) : (
                  <>
                    <h2 className="mt-3 font-display text-3xl font-semibold text-stone-900 sm:text-4xl">Handwerk, das den Raum verändert</h2>
                    <p className="mt-6 text-lg leading-relaxed text-stone-600">{project.description}</p>
                  </>
                )}
              </FadeIn>

              {project.challenges && (
                <FadeIn>
                  <div className="relative mt-12 overflow-hidden rounded-2xl bg-stone-950 p-7 text-white sm:p-10">
                    <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-primary-500/20 blur-2xl" aria-hidden="true" />
                    <Sparkles className="relative h-7 w-7 text-primary-300" />
                    <h2 className="relative mt-5 font-display text-2xl font-semibold sm:text-3xl">Die handwerkliche Herausforderung</h2>
                    <p className="relative mt-4 text-base leading-relaxed text-stone-300 sm:text-lg">{project.challenges}</p>
                  </div>
                </FadeIn>
              )}

              {((project.materials && project.materials.length > 0) || (project.techniques && project.techniques.length > 0)) && (
                <div className="mt-12 grid gap-5 md:grid-cols-2">
                  {project.materials && project.materials.length > 0 && (
                    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-6 sm:p-7">
                      <h2 className="font-display text-xl font-semibold text-stone-900">Materialien</h2>
                      <ul className="mt-5 space-y-3">
                        {project.materials.map((material) => (
                          <li key={material} className="flex items-start gap-3 text-sm leading-relaxed text-stone-600">
                            <span className="mt-0.5 rounded-full bg-primary-100 p-1 text-primary-700"><Check className="h-3.5 w-3.5" /></span>
                            {material}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {project.techniques && project.techniques.length > 0 && (
                    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-6 sm:p-7">
                      <h2 className="font-display text-xl font-semibold text-stone-900">Ausführung</h2>
                      <ul className="mt-5 space-y-3">
                        {project.techniques.map((technique) => (
                          <li key={technique} className="flex items-start gap-3 text-sm leading-relaxed text-stone-600">
                            <span className="mt-0.5 rounded-full bg-primary-100 p-1 text-primary-700"><Check className="h-3.5 w-3.5" /></span>
                            {technique}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <aside className="lg:relative">
              <div className="rounded-2xl border border-stone-200 bg-[#f7f5f1] p-6 sm:p-8 lg:sticky lg:top-24">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-700">Projekt auf einen Blick</p>
                <div className="mt-6 divide-y divide-stone-200">
                  {projectFacts.map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center gap-4 py-4 first:pt-0">
                      <span className="rounded-xl bg-white p-3 text-primary-700 shadow-sm"><Icon className="h-5 w-5" /></span>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-[0.12em] text-stone-500">{label}</p>
                        <p className="mt-0.5 font-semibold text-stone-900">{value}</p>
                      </div>
                    </div>
                  ))}
                  {project.date && (
                    <div className="flex items-center gap-4 py-4">
                      <span className="rounded-xl bg-white p-3 text-primary-700 shadow-sm"><Calendar className="h-5 w-5" /></span>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-[0.12em] text-stone-500">Fertigstellung</p>
                        <p className="mt-0.5 font-semibold text-stone-900">{formatDate(project.date)}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-6 rounded-xl bg-white p-5">
                  <p className="font-display text-xl font-semibold leading-snug text-stone-900">Eine ähnliche Idee für Ihren Raum?</p>
                  <p className="mt-2 text-sm leading-relaxed text-stone-600">Lassen Sie uns unverbindlich über Ihr Vorhaben sprechen.</p>
                  <Link
                    to="/kontakt"
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-primary-700"
                  >
                    Kostenlose Beratung
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-stone-950 py-16 text-white sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col justify-between gap-6 border-b border-white/10 pb-10 sm:flex-row sm:items-end">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-300">Weitere Referenzen</p>
                <h2 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">Noch mehr echte Projekte</h2>
              </div>
              <Link to="/#gallery" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-300 transition hover:text-white">
                Alle Projekte ansehen <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {previousProject ? (
                <Link to={`/projekte/${previousProject.slug}`} className="group rounded-2xl border border-white/10 p-5 transition hover:border-white/25 hover:bg-white/5 sm:p-6">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-stone-500"><ChevronLeft className="h-4 w-4" /> Vorheriges Projekt</span>
                  <span className="mt-3 block font-display text-xl font-semibold text-white transition group-hover:text-primary-200">{previousProject.title}</span>
                </Link>
              ) : <div />}
              {nextProject && (
                <Link to={`/projekte/${nextProject.slug}`} className="group rounded-2xl border border-white/10 p-5 text-right transition hover:border-white/25 hover:bg-white/5 sm:p-6">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-stone-500">Nächstes Projekt <ChevronRight className="h-4 w-4" /></span>
                  <span className="mt-3 block font-display text-xl font-semibold text-white transition group-hover:text-primary-200">{nextProject.title}</span>
                </Link>
              )}
            </div>
          </div>
        </section>
      </article>

      {lightboxIndex !== null && images[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-stone-950/95 p-3 backdrop-blur-sm sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={`Bildergalerie ${project.title}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLightboxIndex(null);
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 sm:right-6 sm:top-6"
            aria-label="Galerie schließen"
          >
            <X className="h-6 w-6" />
          </button>

          {images.length > 1 && (
            <>
              <button type="button" onClick={() => moveLightbox('prev')} className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 sm:left-6 sm:p-4" aria-label="Vorheriges Bild">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button type="button" onClick={() => moveLightbox('next')} className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 sm:right-6 sm:p-4" aria-label="Nächstes Bild">
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <figure className="flex max-h-full max-w-6xl flex-col items-center">
            <img src={images[lightboxIndex]} alt={`${project.title} – Ansicht ${lightboxIndex + 1}`} className="max-h-[82vh] max-w-full rounded-lg object-contain shadow-2xl" />
            <figcaption className="mt-4 text-center text-sm text-stone-300">
              {project.title} <span className="mx-2 text-stone-600">•</span> {lightboxIndex + 1} / {images.length}
            </figcaption>
          </figure>
        </div>
      )}
    </>
  );
};

export default ProjectDetailPage;
